import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Col,
  Row,
  Spinner,
} from "react-bootstrap";
import { BsClock, BsJournalText } from "react-icons/bs";
import { GiStarShuriken } from "react-icons/gi";
import { VscListFlat } from "react-icons/vsc";
import { LuPlus } from "react-icons/lu";
import authService from "../../../utils/authService";
import { fetchBatchesByCourseSingle } from "../../../utils/batchUtils";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import TestFilterSelect from "./TestFilterSelect";
import QuestionSelectionLogicModal, {
  computePaperTotalsFromLogic,
} from "./QuestionSelectionLogicModal";
import TestSlotsPanel, {
  type PaperTotals,
  type TestSlotsPanelHandle,
} from "./TestSlotsPanel";
import type {
  BatchConfigEntry,
  SelectedSubject,
  TestData,
} from "./testTypes";
import { TEST_FLOW_API } from "./testApiPaths";
import {
  DEFAULT_TEST_SECURITY_CONFIGURATION,
  buildTestSecuritySavePayload,
  fetchAllowedCourses,
  loadBatchConfigsFromBackend,
  removeTestBatchAssignment,
  saveTestBatchAssignment,
  saveTestSecurityConfiguration,
  updateTestMetadata,
} from "./testUtils";
import {
  saveAssignTestContext,
  saveConfigureDetailContext,
} from "./testRouteUtils";
import { useTestRoutes } from "./testRoutes";

type TestConfigureDetailPanelProps = {
  test: TestData;
  subjects: SelectedSubject[];
  initialBatchConfigs?: BatchConfigEntry[];
};

const createBatchEntry = (
  track: { track_id: string; track_name: string },
  course: { course_id: string; course_name: string },
  batch: { batch_id: string; batch_name: string },
  subjects: SelectedSubject[],
  testType: string | null
): BatchConfigEntry => ({
  batch_id: batch.batch_id,
  batch_name: batch.batch_name,
  course_id: course.course_id,
  course_name: course.course_name,
  track_id: track.track_id,
  track_name: track.track_name,
  subjects: subjects.map((s) => s.subject_name),
  test_type: testType,
  date: null,
  time: null,
  end_date: null,
  end_time: null,
  securityConfig: { ...DEFAULT_TEST_SECURITY_CONFIGURATION },
  status: "draft",
  assignedStudentIds: [],
  slot_assigned_counts: {},
  securitySaved: false,
  scheduleSaved: false,
});

const TestConfigureDetailPanel: React.FC<TestConfigureDetailPanelProps> = ({
  test: initialTest,
  subjects,
  initialBatchConfigs = [],
}) => {
  const navigate = useNavigate();
  const routes = useTestRoutes();

  const [test, setTest] = useState(initialTest);
  const [batchConfigs, setBatchConfigs] =
    useState<BatchConfigEntry[]>(initialBatchConfigs);
  const [batches, setBatches] = useState<
    Awaited<ReturnType<typeof fetchBatchesByCourseSingle>>
  >([]);
  const [selectedTrack, setSelectedTrack] = useState(initialTest.track_id || "");
  const [selectedCourse, setSelectedCourse] = useState(
    initialTest.course_id || ""
  );
  const [selectedBatch, setSelectedBatch] = useState("");
  const [lockedTrackName, setLockedTrackName] = useState("");
  const [lockedCourseName, setLockedCourseName] = useState("");
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{ variant: "success" | "danger"; message: string } | null>(null);
  const [showLogicModal, setShowLogicModal] = useState(false);
  const [logicSaved, setLogicSaved] = useState(false);
  const slotsPanelRef = useRef<TestSlotsPanelHandle>(null);

  const persistContext = useCallback(() => {
      saveAssignTestContext(test);
      saveConfigureDetailContext({
        test_id: test.test_id,
        subjects,
      });
    },
    [subjects, test]
  );

  useEffect(() => {
    persistContext();
  }, [batchConfigs, persistContext]);

  const applyPaperTotals = useCallback(
    (totals: PaperTotals) => {
      setTest((prev) => {
        const next = {
          ...prev,
          duration: String(totals.duration ?? 0),
          marks: Number(totals.marks ?? 0),
        };
        saveAssignTestContext(next);
        return next;
      });
    },
    []
  );

  const refreshTestTotals = useCallback(async () => {
    try {
      const [testRes, logicRes, rulesRes] = await Promise.all([
        authService.get(`/admin/tests/get/${test.test_id}`),
        authService.get(`${TEST_FLOW_API}/tests/${test.test_id}/selection-logic`),
        authService.get("admin/fetch_rules/"),
      ]);
      const duration = parseInt(String(testRes.data?.duration ?? 0), 10) || 0;
      const marks = Number(testRes.data?.marks ?? 0);
      const logic = logicRes.data?.logic;
      if (logic?.subject_ids?.length) {
        setLogicSaved(true);
      } else {
        setLogicSaved(false);
      }
      if (duration > 0 || marks > 0) {
        applyPaperTotals({ duration, marks });
        return;
      }
      const estimated = computePaperTotalsFromLogic(logic, rulesRes.data);
      if (estimated.minutes > 0 || estimated.marks > 0) {
        applyPaperTotals({
          duration: estimated.minutes,
          marks: estimated.marks,
        });
      }
    } catch (err) {
      console.error("Failed to refresh test duration/marks:", err);
    }
  }, [applyPaperTotals, test.test_id]);

  useEffect(() => {
    void refreshTestTotals();
  }, [refreshTestTotals]);

  useEffect(() => {
    let cancelled = false;

    const hydrateBatchConfigs = async () => {
      try {
        const fromApi = await loadBatchConfigsFromBackend(
          test.test_id,
          subjects,
          test
        );
        if (cancelled) return;

        const apiBatchIds = new Set(fromApi.map((row) => row.batch_id));
        const localOnly = initialBatchConfigs.filter(
          (row) => !apiBatchIds.has(row.batch_id)
        );
        const mergeSlotCounts = (
          a?: Record<string, number>,
          b?: Record<string, number>
        ): Record<string, number> => {
          const out: Record<string, number> = { ...(a || {}) };
          for (const [key, value] of Object.entries(b || {})) {
            out[key] = Math.max(out[key] ?? 0, Number(value) || 0);
          }
          return out;
        };
        // Keep fresher per-slot counts from navigation state after assign.
        const mergedApi = fromApi.map((apiRow) => {
          const local = initialBatchConfigs.find(
            (row) => row.batch_id === apiRow.batch_id
          );
          if (!local) return apiRow;
          const slot_assigned_counts = mergeSlotCounts(
            apiRow.slot_assigned_counts,
            local.slot_assigned_counts
          );
          const assignedStudentIds =
            (local.assignedStudentIds?.length ?? 0) >
            (apiRow.assignedStudentIds?.length ?? 0)
              ? local.assignedStudentIds
              : apiRow.assignedStudentIds;
          const hasAssigned =
            (assignedStudentIds?.length ?? 0) > 0 ||
            Object.values(slot_assigned_counts).some((n) => n > 0);
          let status = apiRow.status;
          if (
            hasAssigned &&
            status === "draft" &&
            local.status !== "draft"
          ) {
            status = local.status === "scheduled" ? "scheduled" : "partial";
          }
          return {
            ...apiRow,
            assignedStudentIds,
            slot_assigned_counts,
            status,
          };
        });
        const merged = [...mergedApi, ...localOnly];
        if (merged.length > 0) {
          setBatchConfigs(merged);
        }
      } catch (error) {
        console.error("Failed to load batch assignments:", error);
      }
    };

    hydrateBatchConfigs();

    return () => {
      cancelled = true;
    };
  }, [initialBatchConfigs, subjects, test]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        // Course name comes from allowed-courses — no courses-by-track call.
        const [allowed, trackData] = await Promise.all([
          fetchAllowedCourses(test.test_id),
          fetchAllTracksForCourses().catch(() => []),
        ]);
        if (cancelled) return;

        const trackFromApi = allowed.track_id || test.track_id || "";
        if (trackFromApi) {
          setSelectedTrack(trackFromApi);
          const trackRow = trackData.find((t) => t.track_id === trackFromApi);
          setLockedTrackName(trackRow?.track_name || trackFromApi);
        }

        if (allowed.courses.length > 0) {
          const only = allowed.courses[0];
          setSelectedCourse(only.course_id);
          setLockedCourseName(only.course_name || only.course_id);
        } else if (test.course_id) {
          setSelectedCourse(test.course_id);
        }
      } catch (err) {
        console.error("Failed to fetch allowed courses:", err);
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [test.test_id, test.track_id, test.course_id]);

  useEffect(() => {
    if (!selectedCourse) {
      setBatches([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingBatches(true);
      setSelectedBatch("");
      try {
        const data = await fetchBatchesByCourseSingle(selectedCourse);
        if (!cancelled) setBatches(data);
      } catch (err) {
        console.error("Failed to fetch batches:", err);
      } finally {
        if (!cancelled) setLoadingBatches(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCourse]);

  const handleAddBatch = async () => {
    const batch = batches.find((b) => b.batch_id === selectedBatch);
    if (!selectedTrack || !selectedCourse || !batch) {
      setAlert({
        variant: "danger",
        message: "Select batch before adding.",
      });
      return;
    }
    const track = {
      track_id: selectedTrack,
      track_name: lockedTrackName || selectedTrack,
    };
    const course = {
      course_id: selectedCourse,
      course_name: lockedCourseName || selectedCourse,
    };
    // Same batch may be attached to multiple slots — only create config once.
    const alreadyInConfigs = batchConfigs.some(
      (b) => b.batch_id === batch.batch_id
    );
    if (!alreadyInConfigs) {
      const entry = createBatchEntry(
        track,
        course,
        batch,
        subjects,
        test.test_type
      );
      setBatchConfigs((prev) => [...prev, entry]);
    }
    const err = await slotsPanelRef.current?.attachBatch(batch.batch_id);
    if (err) {
      if (!alreadyInConfigs) {
        setBatchConfigs((prev) =>
          prev.filter((b) => b.batch_id !== batch.batch_id)
        );
      }
      setAlert({ variant: "danger", message: err });
      return;
    }
    setSelectedBatch("");
    setAlert(
      alreadyInConfigs
        ? {
            variant: "success",
            message: `Batch linked to the open slot. Same batch can be used on multiple slots.`,
          }
        : null
    );
  };

  const handleAssignStudents = async (
    batch: BatchConfigEntry,
    slotId?: number
  ) => {
    try {
      await saveTestBatchAssignment(test, batch);
    } catch (error) {
      console.error("Failed to save batch assignment:", error);
      setAlert({
        variant: "danger",
        message: "Could not save batch assignment. Please try again.",
      });
      return;
    }
    persistContext();
    navigate(
      routes.configureStudents(test.title, {
        test_id: test.test_id,
        batch_id: batch.batch_id,
        course_id: batch.course_id,
        track_id: batch.track_id,
        slot_id: slotId != null ? String(slotId) : undefined,
      }),
      {
        state: {
          test,
          batchContext: {
            track_id: batch.track_id,
            track_name: batch.track_name,
            course_id: batch.course_id,
            course_name: batch.course_name,
            batch_id: batch.batch_id,
            batch_name: batch.batch_name,
          },
          batchConfigs,
          subjects,
          slot_id: slotId,
        },
      }
    );
  };

  const handleRemoveBatch = async (batchId: string) => {
    try {
      await removeTestBatchAssignment(test.test_id, batchId);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      if (status !== 404) {
        console.error("Failed to remove batch assignment:", error);
        setAlert({
          variant: "danger",
          message:
            typeof detail === "string" && detail
              ? detail
              : "Failed to remove batch. Please try again.",
        });
        return;
      }
    }
    setBatchConfigs((prev) => prev.filter((b) => b.batch_id !== batchId));
    setAlert(null);
  };

  const handleSaveAll = async () => {
    if (batchConfigs.length === 0) {
      setAlert({ variant: "danger", message: "Add at least one batch to save." });
      return;
    }
    setSaving(true);
    setAlert(null);
    try {
      for (const batch of batchConfigs) {
        const testPayload: TestData = {
          ...test,
          track_id: batch.track_id,
          course_id: batch.course_id,
          batch_id: batch.batch_id,
          batch: batch.batch_name,
          subject: batch.subjects[0] ?? test.subject,
          test_type: test.test_type,
          date: batch.date,
          time: batch.time,
          end_date: batch.end_date ?? null,
          end_time: batch.end_time ?? null,
        };
        await updateTestMetadata(testPayload, test);
        await saveTestSecurityConfiguration(
          test.test_id,
          buildTestSecuritySavePayload(
            batch.securityConfig.auto_release_test_report,
            batch.batch_id
          )
        );
      }
      setAlert({ variant: "success", message: "All changes saved successfully." });
    } catch (error) {
      console.error("Save all failed:", error);
      setAlert({ variant: "danger", message: "Failed to save changes." });
    } finally {
      setSaving(false);
    }
  };

  const subjectLabel =
    subjects.map((s) => s.subject_name).join(", ") || test.subject || "N/A";
  const courseDefinedFinal =
    test.test_type === "Final Test" && subjects.length <= 1;

  return (
    <div className="d-flex flex-column h-100 pb-2">
      {alert && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() => setAlert(null)}
          className="mb-2"
        >
          {alert.message}
        </Alert>
      )}

      <div className="border-bottom pb-2 mb-2">
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div className="min-w-0 flex-grow-1">
            <h6 className="mb-0 fw-semibold text-break pt-2">{test.title}</h6>
            <div className="d-flex flex-wrap gap-3 mt-2 text-secondary small">
              <span className="d-inline-flex align-items-center gap-1">
                <VscListFlat />
                {subjectLabel}
              </span>
              <span className="d-inline-flex align-items-center gap-1">
                <BsClock />
                {test.duration}{" "}
                {test.duration === "1" ? "Minute" : "Minutes"}
              </span>
              <span className="d-inline-flex align-items-center gap-1">
                <GiStarShuriken />
                {test.marks} Marks
              </span>
              {test.test_type && (
                <Badge bg="secondary" className="align-self-center">
                  {test.test_type}
                </Badge>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0 pt-1">
            <span className="small fw-semibold text-secondary text-nowrap">
              Selection logic :
            </span>
            <Button
              variant={logicSaved ? "success" : "outline-primary"}
              size="sm"
              className="px-2 py-1 d-inline-flex align-items-center justify-content-center"
              title={
                courseDefinedFinal
                  ? "View selection logic"
                  : logicSaved
                    ? "Selection logic (saved for all schedules)"
                    : "Configure selection logic"
              }
              onClick={() => setShowLogicModal(true)}
            >
              <BsJournalText />
            </Button>
          </div>
        </div>
      </div>

      <div className="fw-semibold small text-secondary mb-2">
        Batches for this test ({batchConfigs.length})
      </div>
      <Row className="g-3 align-items-stretch mb-3">
        <Col md={6}>
          <div className="d-flex flex-column gap-2 h-100">
            <div className="small">
              <span className="text-secondary fw-semibold">Track:</span>{" "}
              <span className="text-dark">
                {loadingMeta
                  ? "Loading…"
                  : lockedTrackName || selectedTrack || "—"}
              </span>
            </div>
            <div className="small">
              <span className="text-secondary fw-semibold">Course:</span>{" "}
              <span className="text-dark">
                {loadingMeta
                  ? "Loading…"
                  : lockedCourseName || selectedCourse || "—"}
              </span>
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div className="d-flex flex-column gap-2">
            <TestFilterSelect
              label="Batch"
              value={selectedBatch}
              onChange={setSelectedBatch}
              disabled={!selectedCourse || loadingBatches}
              placeholder="Select Batch"
              options={batches.map((b) => ({
                value: b.batch_id,
                label: b.batch_name,
              }))}
              fullWidth
            />
            <Button
              variant="primary"
              size="sm"
              className="align-self-end d-inline-flex align-items-center justify-content-center gap-1 py-0 px-2"
              onClick={handleAddBatch}
              disabled={!selectedTrack || !selectedCourse || !selectedBatch}
            >
              <LuPlus size={12} /> Add Batch
            </Button>
          </div>
        </Col>
      </Row>

      <TestSlotsPanel
        ref={slotsPanelRef}
        testId={test.test_id}
        testName={test.title}
        testType={test.test_type}
        logicSaved={logicSaved}
        batchConfigs={batchConfigs}
        onAssignStudents={handleAssignStudents}
        onRemoveBatch={handleRemoveBatch}
        onSlotsChanged={(totals) => {
          if (totals) applyPaperTotals(totals);
          else void refreshTestTotals();
        }}
      />

      <div className="border-top bg-white sticky-bottom py-3 d-flex justify-content-end">
        <Button variant="primary" onClick={handleSaveAll} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>

      <QuestionSelectionLogicModal
        show={showLogicModal}
        testId={test.test_id}
        testName={test.title}
        testType={test.test_type}
        subjects={subjects}
        batchNames={batchConfigs.map((b) => b.batch_name)}
        readOnly={courseDefinedFinal}
        onHide={() => setShowLogicModal(false)}
        onSaved={(_logic, description, totals) => {
          setLogicSaved(true);
          setTest((prev) => {
            const next = {
              ...prev,
              ...(description ? { description } : {}),
              duration: String(totals?.duration ?? 0),
              marks: Number(totals?.marks ?? 0),
            };
            saveAssignTestContext(next);
            return next;
          });
          setAlert({
            variant: "success",
            message: "Selection logic saved. Regenerate slot questions.",
          });
        }}
      />
    </div>
  );
};

export default TestConfigureDetailPanel;
